import React, { useState } from 'react'
import { Table } from 'antd'
import type { TableProps, ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { useTheme, useThemeColors } from '../../ThemeContext'

interface AntTableProps<T> extends TableProps<T> {
	columns: ColumnsType<T>
	dataSource: T[]
	rowKey: string | ((record: T) => string)
	initialPageSize?: number
	pageSizeOptions?: string[]
	headerColor?: string // Add a custom prop for header color
	serverPagination?: boolean
	paginationState?: TablePaginationConfig
	onPaginationChange?: (pagination: TablePaginationConfig) => void
}

export function AntTable<T extends object>({
	columns,
	dataSource,
	rowKey,
	initialPageSize = 10,
	pageSizeOptions = ['5', '10', '20', '50'],
	headerColor, // undefined by default → resolved from theme below
	serverPagination = false,
	paginationState,
	onPaginationChange,
	...rest
}: AntTableProps<T>) {
	const themeColors = useThemeColors()
	const { isDarkMode } = useTheme()
	const { primary } = themeColors
	const resolvedHeaderColor = headerColor ?? (isDarkMode ? themeColors.subBg : primary)
	const [localPagination, setLocalPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: initialPageSize,
		total: dataSource.length,
	})

	const resolvedPagination = serverPagination
		? {
				current: paginationState?.current || 1,
				pageSize: paginationState?.pageSize || initialPageSize,
				total: paginationState?.total || 0,
			}
		: {
				current: localPagination.current || 1,
				pageSize: localPagination.pageSize || initialPageSize,
				total: dataSource.length,
			}

	const handleTableChange: TableProps<T>['onChange'] = (newPagination) => {
		if (serverPagination) {
			onPaginationChange?.(newPagination)
			return
		}
		setLocalPagination(newPagination)
	}

	// Add a column for row numbers
	const rowNumberColumn = {
		title: 'No.', // Header text for the column
		dataIndex: 'rowNumber',
		key: 'rowNumber',
		render: (_: any, __: T, index: number) =>
			((resolvedPagination.current || 1) - 1) *
				(resolvedPagination.pageSize || initialPageSize) +
			index +
			1, // Calculate row number
		align: 'center' as 'center', // Align numbers to the left
	}

	// Update all columns to align text to the left
	const alignedColumns = columns.map((column) => ({
		...column,
		align: 'left' as 'left', // Ensure text alignment is left
	}))

	return (
		<Table<T>
			columns={[rowNumberColumn, ...alignedColumns]} // Include the row number column
			dataSource={dataSource}
			rowKey={rowKey}
			pagination={{
				current: resolvedPagination.current,
				pageSize: resolvedPagination.pageSize,
				total: resolvedPagination.total,
				showSizeChanger: true,
				pageSizeOptions,
				showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
			}}
			onChange={handleTableChange}
			components={{
				header: {
					cell: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
						<th
							{...props}
							style={{
								backgroundColor: resolvedHeaderColor,
								color: isDarkMode ? themeColors.text : '#fff', // Set header text color
								textAlign: 'center', // Align text to the left
								fontWeight: 'bold', // Make the header bold
								borderBottom: isDarkMode ? `1px solid ${themeColors.border}` : 'none'
							}}
						/>
					),
				},
			}}
			{...rest}
		/>
	)
}
